import { useMemo, useState } from 'react'

import { computeAscentDescentMeters, getActiveElevationSource } from '@fit-editor/fit-core'
import type { ActivityRecord, SummaryValue } from '@fit-editor/fit-core'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCurrentActivity } from '@/hooks/use-current-activity'
import { useOperationPreview } from '@/hooks/use-operation-preview'
import { useTerrainOverlay } from '@/hooks/use-terrain-overlay'
import { useUnits } from '@/hooks/use-units'
import { formatElevationMeters } from '@/lib/format'
import type { UnitSystem } from '@/lib/format'
import {
  buildSetElevationPlan,
  countElevationChanges,
  describeTerrainResolution,
  hasAnyDeviceAltitude,
} from '@/lib/terrain'
import type { ElevationMode } from '@/lib/terrain'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'

export interface ApplyTerrainElevationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Scope = 'all' | 'selection'

const MODE_LABELS: Record<ElevationMode, { label: string; hint: string }> = {
  fillMissing: {
    label: 'Fill missing only',
    hint: 'Only records with no device elevation get the terrain value.',
  },
  replace: {
    label: 'Replace',
    hint: 'Every record in scope with a terrain value has its elevation overwritten.',
  },
}

function formatSummary(value: SummaryValue<number>, units: UnitSystem): string {
  return value.value == null ? 'unavailable' : formatElevationMeters(value.value, units)
}

function ascentDescent(records: ActivityRecord[], elevationEstimated: boolean) {
  return computeAscentDescentMeters(records, { elevationEstimated })
}

/**
 * Step 2 of the terrain feature: writes the already-loaded overlay's
 * values into the file as an undoable `setElevation` edit. No network
 * request -- the samples come straight from the overlay (and are embedded
 * in the op, so undo/redo replay is deterministic and offline). Follows
 * the `RangeOperationDialog` pattern: live preview via
 * `useOperationPreview`, and the dialog stays open if applying fails.
 */
export function ApplyTerrainElevationDialog({
  open,
  onOpenChange,
}: ApplyTerrainElevationDialogProps) {
  const loaded = useActivityStore(selectSelectedActivity)
  const selectedRangeMs = useActivityStore((s) => s.selectedRangeMs)
  const applyOperationToSelected = useActivityStore((s) => s.applyOperationToSelected)

  const activeLoaded = open ? loaded : undefined
  const { result } = useCurrentActivity(activeLoaded?.history)
  const { terrain, overlay } = useTerrainOverlay(activeLoaded)

  const [scopeChoice, setScopeChoice] = useState<Scope | null>(null)
  const [modeChoice, setModeChoice] = useState<ElevationMode | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Reset to the defaults on every open (React's "adjust state when a prop
  // changes" pattern -- see `RangeOperationDialog`).
  const [prevOpen, setPrevOpen] = useState(false)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setScopeChoice(null)
      setModeChoice(null)
    }
  }

  const records = result?.activity.records
  const deviceHasAltitude = useMemo(
    () => (records == null ? false : hasAnyDeviceAltitude(records)),
    [records],
  )
  const scope: Scope = scopeChoice ?? (selectedRangeMs != null ? 'selection' : 'all')
  const mode: ElevationMode = modeChoice ?? (deviceHasAltitude ? 'fillMissing' : 'replace')
  const range = scope === 'selection' ? selectedRangeMs : null
  const scopeValid = scope === 'all' || selectedRangeMs != null
  const source = terrain.source

  const aligned = records != null && overlay != null && overlay.values.length === records.length
  const plan = useMemo(
    () =>
      aligned && source != null && scopeValid
        ? buildSetElevationPlan({
            records,
            terrainValues: overlay.values,
            range,
            mode,
            source,
          })
        : null,
    [aligned, records, overlay, range, mode, source, scopeValid],
  )
  const operation = plan?.operation ?? null

  const preview = useOperationPreview(activeLoaded?.history, operation)

  const beforeEstimated =
    activeLoaded != null && getActiveElevationSource(activeLoaded.history) != null
  const previewStats = useMemo(() => {
    if (preview.baseline == null || preview.preview == null) return null
    const before = preview.baseline.activity.records
    const after = preview.preview.activity.records
    return {
      changed: countElevationChanges(before, after),
      // "Before" keeps whatever provenance the current state already has
      // (estimated only if an earlier setElevation is active); "after" is
      // estimated by definition -- terrain data is now in the file.
      before: ascentDescent(before, beforeEstimated),
      after: ascentDescent(after, true),
      newWarnings: preview.preview.warnings.slice(preview.baseline.warnings.length),
    }
  }, [preview.baseline, preview.preview, beforeEstimated])

  if (loaded == null) return null

  const handleConfirm = async () => {
    if (operation == null) return
    setSubmitting(true)
    try {
      // `null` = the apply failed (the store already showed a toast) --
      // stay open with the user's choices intact.
      const warnings = await applyOperationToSelected(operation)
      if (warnings != null) onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  const overlayReady = source != null && terrain.status !== 'error'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="apply-terrain-description" className="max-w-lg">
        <DialogTitle>Apply terrain elevation to file</DialogTitle>
        <DialogDescription id="apply-terrain-description">
          Writes the loaded terrain elevation into the activity&apos;s elevation fields as an
          undoable edit. Uses the values already shown on the timeline &ndash; no new network
          request. Ascent/descent will be marked as estimated.
        </DialogDescription>

        {!overlayReady ? (
          <p role="status" className="text-muted-foreground text-sm">
            Load terrain elevation for this activity first (timeline header or Edit menu).
          </p>
        ) : records == null ? (
          <p role="status" className="text-muted-foreground text-sm">
            Loading activity…
          </p>
        ) : (
          <div className="flex flex-col gap-3 text-sm">
            <fieldset className="flex flex-col gap-1">
              <legend className="text-foreground mb-1 font-medium">Scope</legend>
              <label className="text-foreground flex items-center gap-2">
                <input
                  type="radio"
                  name="terrain-scope"
                  value="all"
                  checked={scope === 'all'}
                  onChange={() => setScopeChoice('all')}
                />
                Whole activity
              </label>
              <label
                className={`flex items-center gap-2 ${selectedRangeMs == null ? 'text-muted-foreground' : 'text-foreground'}`}
              >
                <input
                  type="radio"
                  name="terrain-scope"
                  value="selection"
                  checked={scope === 'selection'}
                  disabled={selectedRangeMs == null}
                  onChange={() => setScopeChoice('selection')}
                />
                Current timeline selection
                {selectedRangeMs == null
                  ? ' (select a range on the timeline first)'
                  : ` (${new Date(selectedRangeMs.start).toLocaleTimeString()} – ${new Date(selectedRangeMs.end).toLocaleTimeString()})`}
              </label>
            </fieldset>

            <fieldset className="flex flex-col gap-1">
              <legend className="text-foreground mb-1 font-medium">Mode</legend>
              {(Object.keys(MODE_LABELS) as ElevationMode[]).map((m) => (
                <label key={m} className="text-foreground flex items-start gap-2">
                  <input
                    type="radio"
                    name="terrain-mode"
                    value={m}
                    checked={mode === m}
                    onChange={() => setModeChoice(m)}
                    aria-describedby={`terrain-mode-${m}-hint`}
                    className="mt-1"
                  />
                  <span>
                    {MODE_LABELS[m].label}
                    <span
                      id={`terrain-mode-${m}-hint`}
                      className="text-muted-foreground block text-xs"
                    >
                      {MODE_LABELS[m].hint}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>

            <p className="text-muted-foreground text-xs">
              Source: {source.name}, {describeTerrainResolution(source)}. Terrain is the ground
              surface &ndash; bridges, tunnels and indoor sections will be wrong.
            </p>

            <ApplyPreview
              ready={plan != null}
              noValues={plan != null && plan.operation == null}
              skippedCount={plan?.skippedCount ?? 0}
              error={preview.error}
              stats={previewStats}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={operation == null || submitting}
            onClick={() => void handleConfirm()}
          >
            Apply to file
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface ApplyPreviewProps {
  ready: boolean
  noValues: boolean
  skippedCount: number
  error: string | null
  stats: {
    changed: number
    before: ReturnType<typeof ascentDescent>
    after: ReturnType<typeof ascentDescent>
    newWarnings: { message: string }[]
  } | null
}

function ApplyPreview({ ready, noValues, skippedCount, error, stats }: ApplyPreviewProps) {
  const units = useUnits()
  if (!ready) {
    return (
      <p role="status" className="text-muted-foreground text-xs">
        Preparing terrain values…
      </p>
    )
  }
  if (noValues) {
    return (
      <p role="status" className="text-muted-foreground text-xs">
        No terrain values in this scope &ndash; nothing to apply.
      </p>
    )
  }
  if (error != null) {
    return (
      <div
        role="alert"
        className="border-destructive/50 bg-destructive/10 flex flex-col gap-1 rounded-md border p-2 text-xs"
      >
        <p className="text-destructive font-medium">Could not compute a preview.</p>
        <p className="text-destructive">{error}</p>
      </div>
    )
  }
  if (stats == null) {
    return (
      <p role="status" className="text-muted-foreground text-xs">
        Calculating preview…
      </p>
    )
  }
  return (
    <div
      role="status"
      className="border-border bg-background flex flex-col gap-1 rounded-md border p-2 text-xs"
    >
      <p className="text-foreground font-medium">Preview</p>
      <p className="text-muted-foreground">Records with changed elevation: {stats.changed}</p>
      {skippedCount > 0 && (
        <p className="text-muted-foreground">
          {skippedCount === 1 ? '1 record in scope has' : `${skippedCount} records in scope have`}{' '}
          no terrain value and will be left unchanged.
        </p>
      )}
      <p className="text-muted-foreground">
        Ascent: {formatSummary(stats.before.ascent, units)} &rarr;{' '}
        {formatSummary(stats.after.ascent, units)}
      </p>
      <p className="text-muted-foreground">
        Descent: {formatSummary(stats.before.descent, units)} &rarr;{' '}
        {formatSummary(stats.after.descent, units)}
      </p>
      {stats.newWarnings.length > 0 && (
        <p className="text-warning-text bg-warning/20 mt-1 rounded-sm p-1.5">
          {stats.newWarnings.map((w) => w.message).join(' ')}
        </p>
      )}
    </div>
  )
}
